/**
 * useVersionDisplay (T29.2.4) — the ONE place the live-vs-frozen-vs-web version
 * decision lives. Both version surfaces (Settings → About and the login footer)
 * render its result and hold NO mode inference of their own (D2).
 *
 * The decision keys on ONE reliable signal: `import.meta.env.DEV` (D1). Vite sets
 * it true only when the bundle is served by `vite dev`, and it can't drift — so a
 * genuinely-live local session always reads as live, and a frozen bundled APK
 * always reads as the baked number. The pre-T29.2.4 surfaces inferred the mode
 * from `channel === 'local' && liveBuild !== null` (a printed build label plus an
 * async read), which let a live session fall back to the installed APK's number
 * when the live read hadn't resolved. `channel` is now a label only and
 * `liveBuild` a value source only — neither gates the mode.
 *
 * The returned `kind` discriminates the three render shapes:
 *   - `live-web`    — `import.meta.env.DEV` true. The running web bundle is live
 *                     (Vite dev). `build` is the live stamp when resolved, else
 *                     the baked stamp; `native` rides along when the live session
 *                     runs inside the Capacitor shell (local live-reload), but the
 *                     surface still renders the Web row from `build`, never the
 *                     installed APK's number (D3).
 *   - `native-shell`— not DEV + a native shell present. The installed APK is the
 *                     app; `native` carries its identity, `build` is the bundled
 *                     stamp (used for the drift comparison + the meta row).
 *   - `baked-web`   — not DEV + no native shell. A deployed (or built) web bundle;
 *                     `build` is the baked stamp.
 *
 * Every value the surfaces need is on the result: the build to show (`build`),
 * its `channel`, the optional `native` identity, and the precomputed `hasDrift`
 * (the running web bundle is newer than the installed shell — dev live-reload).
 */

import { useLiveAppBuild } from './useLiveAppBuild';
import { useNativeAppVersion, type NativeAppVersion } from './useNativeAppVersion';
import type { AppBuild } from './appVersion';

export type VersionDisplayKind = 'live-web' | 'native-shell' | 'baked-web';

export interface VersionDisplay {
  /** Which render shape the surfaces use (D2). */
  kind: VersionDisplayKind;
  /**
   * The running web bundle's build stamp — the live stamp under `vite dev` (when
   * resolved), else the baked `__APP_BUILD__`. This is the Web-row source on the
   * `live-web` / `baked-web` shapes, and the drift/meta source on `native-shell`.
   */
  build: AppBuild;
  /** The build's channel — a printed label only, never a gate (D1). */
  channel: string;
  /**
   * The installed native shell's identity (APK versionName/versionCode), or null
   * on web. Present on `native-shell` (drives the Mobile row) and possibly on
   * `live-web` (local live-reload inside the shell — the surface ignores it for
   * the row but it's here for completeness).
   */
  native: NativeAppVersion | null;
  /**
   * Drift — the running web bundle's build differs from the installed shell's
   * build (dev live-reload). Only ever true on `native-shell`; false otherwise.
   */
  hasDrift: boolean;
}

/**
 * Decide the version display from `import.meta.env.DEV` alone (D1). Pure-ish: it
 * composes the two existing reads (`useLiveAppBuild`, `useNativeAppVersion`) but
 * the live-vs-frozen branch is the synchronous `DEV` flag, so the mode never
 * flips while an async read settles.
 */
export function useVersionDisplay(): VersionDisplay {
  const liveBuild = useLiveAppBuild();
  const nativeVersion = useNativeAppVersion();

  // The running web bundle's stamp: the live stamp (local `vite dev`) when it has
  // resolved, else the baked stamp. A value source only — it does NOT decide the
  // mode (D1).
  const build: AppBuild = liveBuild ?? __APP_BUILD__;

  // THE decision: served by `vite dev` → the web bundle is live. Synchronous and
  // drift-proof; `channel`/`liveBuild` no longer gate it.
  if (import.meta.env.DEV) {
    // Local live-reload may run inside the Capacitor shell; the surface still
    // renders the Web row from `build` (never the installed APK's number, D3), so
    // there is no drift row on the local path.
    return {
      kind: 'live-web',
      build,
      channel: build.channel,
      native: nativeVersion,
      hasDrift: false,
    };
  }

  // Frozen bundle. Inside the native shell the installed APK is the app.
  if (nativeVersion !== null) {
    return {
      kind: 'native-shell',
      build: __APP_BUILD__,
      channel: __APP_BUILD__.channel,
      native: nativeVersion,
      // Drift: the running web bundle is newer than the installed shell.
      hasDrift: nativeVersion.build !== __APP_BUILD__.build,
    };
  }

  // A deployed / built web bundle, no native shell.
  return {
    kind: 'baked-web',
    build: __APP_BUILD__,
    channel: __APP_BUILD__.channel,
    native: null,
    hasDrift: false,
  };
}
